import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Faq.module.scss';
import { FAQ_CATEGORIES, FAQ_ITEMS } from './faqData';
import { useInView } from '../../hooks/useInView';

// 질문 위 카테고리 표시(qMeta)는 탭 이름(FAQ_CATEGORIES.label)을 그대로 사용
const CATEGORY_LABEL = new Map(FAQ_CATEGORIES.map((c) => [c.id, c.label]));

function Faq() {
  const [activeCategory, setActiveCategory] = useState('all');
  const cta = useInView<HTMLElement>();
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(FAQ_ITEMS[0]?.id ?? null);

  const filteredItems = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return FAQ_ITEMS.filter((item) => {
      const matchCategory = activeCategory === 'all' || item.categoryId === activeCategory;
      const haystack = `${item.question} ${item.keywords}`.toLowerCase();
      const matchKeyword = !kw || haystack.includes(kw);
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, search]);

  return (
    <div className={styles.page}>
      {/* PAGE HEAD */}
      <div className={styles.pageHeadWrap}>
        <section className={styles.pageHead}>
          <div className={styles.crumb}>
            <Link to="/">HOME</Link>
            <svg viewBox="0 0 24 24">
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span className={styles.crumbHere}>자주 묻는 질문</span>
          </div>
          {/* <span className={styles.eyebrow}>FAQ</span> */}
          <h1 className={styles.pageTitle}>
            Frequently <em>Asked</em>
          </h1>
          <p className={styles.pageDesc}>
            패키지 · 케이스 제작 과정에서 자주 묻는 질문들을 한 곳에 모았습니다. 찾으시는 답변이 없으시면, 언제든
            1:1 상담이나 견적문의로 남겨주세요.
          </p>
        </section>
      </div>

      {/* TOOLBAR */}
      <div className={styles.toolbar}>
        <div className={styles.searchRow}>
          <div className={`${styles.searchWrap} ${search ? styles.hasText : ''}`}>
            <svg viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="키워드를 입력하세요. (예: 결제, 샘플, 목형, 견적)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              className={styles.clearBtn}
              aria-label="초기화"
              onClick={() => setSearch('')}
            >
              <svg viewBox="0 0 24 24">
                <path d="m6 6 12 12M6 18 18 6" />
              </svg>
            </button>
          </div>
          <div className={styles.resultCount}>
            <strong>{filteredItems.length}</strong>개의 결과
          </div>
        </div>

        <div className={styles.tabs}>
          {FAQ_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`${styles.tab} ${activeCategory === cat.id ? styles.active : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* ACCORDION LIST */}
      <div className={styles.faqList}>
        {filteredItems.length === 0 ? (
          <p className={styles.emptyState}>검색 결과가 없습니다. 다른 키워드로 시도해보세요.</p>
        ) : (
          filteredItems.map((item, i) => {
            const isOpen = openId === item.id;
            return (
              <div
                key={item.id}
                className={`${styles.faqItem} ${isOpen ? styles.open : ''}`}
                style={{ '--i': i } as React.CSSProperties}
              >
                <button
                  type="button"
                  className={styles.faqQ}
                  onClick={() => setOpenId(isOpen ? null : item.id)}
                >
                  <span className={styles.qNum}>{String(i + 1).padStart(2, '0')}</span>
                  <span className={styles.qBody}>
                    <span className={styles.qMeta}>{CATEGORY_LABEL.get(item.categoryId)}</span>
                    <span className={styles.qText}>{item.question}</span>
                  </span>
                  <span className={styles.qIcon}>
                    <svg viewBox="0 0 24 24">
                      <line className={styles.vBar} x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </span>
                </button>
                <div className={styles.faqA}>
                  <div className={styles.faqAInner}>
                    {item.paragraphs?.map((p) => <p key={p}>{p}</p>)}
                    {item.list && (
                      <ul>
                        {item.list.map((li) => (
                          <li key={li}>{li}</li>
                        ))}
                      </ul>
                    )}
                    {item.tip && (
                      <span className={styles.aTip}>
                        <svg viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {item.tip}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* CTA */}
      <section ref={cta.ref} className={`${styles.cta} ${cta.inView ? styles.in : ''}`}>
        <span className={styles.ctaEyebrow}>STILL HAVE QUESTIONS?</span>
        <h2 className={styles.ctaTitle}>
          찾으시는 답변이
          <br />
          아직 없으신가요?
        </h2>
        <p className={styles.ctaDesc}>담당 디자이너가 직접 확인합니다. 견적문의와 함께 자유롭게 질문해 주세요.</p>
        <div className={styles.ctaActions}>
          <a className={styles.btnLight} href="tel:031-925-6547">
            전화 문의
          </a>
          <Link className={styles.btnSolid} to="/quote">
            견적 문의하기 →
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Faq;
